import type { RyptError } from './errors';
import { formatTs } from '../lib/format';
import type { ErrorCode } from './types';

/**
 * Every error code rypt can return, and what the dashboard does about it.
 *
 * `satisfies Record<ErrorCode, CodeBehaviour>` is the point: ErrorCode comes from the
 * generated schema, so if the API adds a 47th code this file stops compiling until
 * someone decides what it means. A default branch would have swallowed it silently.
 *
 * Copy follows the brand's error pattern: state what failed, name the object, give the
 * fix. No apology, no "something went wrong", never blame the reader.
 */
export interface CodeBehaviour {
  status: number;
  /** Whether this should send the user back through Auth0. See the note in errors.ts. */
  reauth: boolean;
  retry: 'never' | 'blind-backoff' | 'once';
  surface: 'redirect' | 'app-page' | 'route-page' | 'field' | 'banner' | 'toast' | 'bug-banner';
  field?: 'name' | 'tier' | 'period' | 'window' | 'allowedIps' | 'keyIds' | 'actions' | 'firewall' | 'keepAtMost';
  /** Whether a user can hit this at all, or it would mean the dashboard is broken. */
  reach: 'expected' | 'client-bug' | 'unreachable';
  title: string;
  copy: (e: RyptError) => string;
  /**
   * The line once the automatic retries are used up, for a code retried with backoff:
   * `copy` says a retry is under way, which by then is no longer true. No attempt count.
   */
  finalCopy?: string;
}

const num = new Intl.NumberFormat('en-GB');

/** When a free key's cap resets, from the 429's own `resets_at` where it carried one. */
const resetsPhrase = (e: RyptError): string =>
  e.resetsAt ? `at ${formatTs(e.resetsAt)}` : 'on the first of next month, 00:00 UTC';

/** The key a call was about, quoted, or a plain phrase when the call did not name it. */
const keyNamed = (e: RyptError, fallback = 'this key'): string =>
  e.ctx.keyName === undefined ? fallback : `"${e.ctx.keyName}"`;

/** The version a retire or a restore named, as words: "version 2 of "orders"". */
const versionOf = (e: RyptError): string => `version ${e.ctx.version ?? ''} of ${keyNamed(e, 'the key')}`;

/** The two routes that act on one version of a key, which refuse in their own words. */
const onVersion = (e: RyptError): 'retire' | 'restore' | null =>
  e.ctx.op === 'retireKeyVersion' ? 'retire' : e.ctx.op === 'restoreKeyVersion' ? 'restore' : null;

/*
 * No message carries the request id. Every InlineError and card that shows an error
 * prints the id beside it, so a message that also carried it would say it twice. That
 * holds for invalid_request too: no form puts it on a field, and each sends it to its
 * banner, an InlineError.
 */
export const ERROR_BEHAVIOUR = {
  unauthorized: {
    status: 401, reauth: true, retry: 'never', surface: 'redirect', reach: 'expected',
    title: 'Signing in again',
    copy: () =>
      'unauthorized: rypt rejected this session’s token. Signing in again — you will come back to this page.',
  },

  // The three 403s. None of them is "sign in again": the credential is valid and the
  // wrong kind, or the account has no tenant. Re-authenticating changes nothing.
  api_token_required: {
    status: 403, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'That call needs an API token',
    copy: (e) =>
      `api_token_required: ${e.ctx.op} takes an API token, and the dashboard signs in as you. encrypt, decrypt, wrap, unwrap and rewrap are machine operations — run them from your own machine with an API token from the API tokens page. Signing out will not help.`,
  },
  session_required: {
    status: 403, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'That call needs a console session',
    copy: (e) =>
      `session_required: ${e.ctx.op} takes a console session and this request carried an API token. The dashboard never holds one, so this is a dashboard fault. Reload.`,
  },
  // An API token's restrictions refuse an API token, and the dashboard never holds one: a
  // console session is restricted by nothing. Either one arriving here is a fault.
  ip_not_allowed: {
    status: 403, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: (e) =>
      `ip_not_allowed: ${e.ctx.op} was refused for an API token used from outside its firewall’s networks, and the dashboard signs in as you, never with an API token, so this is a dashboard fault. Reload.`,
  },
  key_not_allowed: {
    status: 403, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: (e) =>
      `key_not_allowed: ${e.ctx.op} was refused for an API token restricted to other keys, and the dashboard signs in as you, never with an API token, so this is a dashboard fault. Reload.`,
  },
  action_not_allowed: {
    status: 403, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: (e) =>
      `action_not_allowed: ${e.ctx.op} was refused for an API token that was not given that action, and the dashboard signs in as you, never with an API token, so this is a dashboard fault. Reload.`,
  },
  no_tenant: {
    status: 403, reauth: false, retry: 'never', surface: 'app-page', reach: 'expected',
    title: 'This account has no tenant',
    copy: () =>
      'no_tenant: your account exists and its membership was removed, so there is no tenant for rypt to act on. Signing in again will not restore it. Ask the operator who removed it, or sign in as a different account.',
  },

  // On the two routes about one version, a 404 is about the version as often as the key:
  // one destroyed since the page read it, at the end of its seven days, is no longer
  // there to retire or restore. The mutation reads the key again, and the read says which.
  not_found: {
    status: 404, reauth: false, retry: 'never', surface: 'route-page', reach: 'expected',
    title: 'No such key',
    copy: (e) => {
      const which = onVersion(e);
      return which
        ? `not_found: rypt found no ${versionOf(e)} that still decrypts, so it did not ${which} it. The version, or the whole key, was destroyed since this page read it. The page is reading the key again.`
        : `not_found: no key ${e.ctx.keyId ?? ''} in this tenant. It was destroyed, it belongs to another tenant, or it never existed — rypt does not say which.`.replace('  ', ' ');
    },
  },
  method_not_allowed: {
    status: 405, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'Wrong method',
    copy: (e) => `method_not_allowed: ${e.ctx.op} reached a route that does not take that method. A dashboard fault, not your account.`,
  },

  invalid_request: {
    status: 400, reauth: false, retry: 'never', surface: 'field', reach: 'client-bug',
    title: 'The request was rejected',
    copy: (e) => `invalid_request: ${e.message}. The dashboard built this body.`,
  },
  invalid_tier: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'tier', reach: 'client-bug',
    title: 'Not a tier',
    copy: () => 'invalid_tier: tier is free, wrapped, software or hsm, and is fixed at creation.',
  },
  invalid_name: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'name', reach: 'expected',
    title: 'Not a usable name',
    copy: () =>
      'invalid_name: a name is 1 to 128 characters with no control characters, and not only whitespace.',
  },
  invalid_period: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'period', reach: 'expected',
    title: 'Not a month',
    copy: (e) => `invalid_period: ${e.ctx.period ?? 'that value'} is not a month. Use YYYY-MM, for example 2026-09.`,
  },
  // The two restrictions of an API token. The API's message names the entry it refused,
  // so it is quoted; the fix after it is the field's.
  invalid_allowed_ips: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'allowedIps', reach: 'expected',
    title: 'Not a usable address',
    copy: (e) =>
      `invalid_allowed_ips: ${e.message}. Put one address or network on each line, for example 203.0.113.7 or 203.0.113.0/24.`,
  },
  invalid_key_ids: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'keyIds', reach: 'expected',
    title: 'Not one of this container’s keys',
    copy: (e) =>
      `invalid_key_ids: ${e.message}. A key destroyed since this page read the list cannot be named; the list is being read again, so choose again from it.`,
  },
  invalid_allowed_actions: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'actions', reach: 'client-bug',
    title: 'Not an action',
    copy: (e) =>
      `invalid_allowed_actions: ${e.message}. The actions are read, encrypt, decrypt, wrap, unwrap, rewrap, rotate, create, rename, delete, restore and retire, and the dashboard offers no others, so this is a dashboard fault. Reload.`,
  },
  // The setting for how many versions a rotation keeps. The dialog checks the number
  // before it sends it, so this reaching the field means the two checks disagree.
  invalid_keep_at_most: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'keepAtMost', reach: 'client-bug',
    title: 'Not a number of versions',
    copy: (e) =>
      `invalid_keep_at_most: rypt did not change how many versions ${keyNamed(e)} keeps, because the number was not a whole number from 1 up. Enter a whole number from 1 up, or turn automatic retiring off.`,
  },
  invalid_container: {
    status: 400, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'No such container',
    copy: (e) =>
      `invalid_container: ${e.message}. It was deleted since this page read it, or it is not one of this tenant’s. The list of containers is being read again; choose one from it.`,
  },
  invalid_firewall: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'firewall', reach: 'expected',
    title: 'Not one of this container’s firewalls',
    copy: (e) =>
      `invalid_firewall: ${e.message}. A firewall deleted since this page read the list cannot be used; the list is being read again, so choose again from it.`,
  },
  invalid_query: {
    status: 400, reauth: false, retry: 'never', surface: 'field', field: 'window', reach: 'expected',
    title: 'The window was rejected',
    copy: (e) =>
      `invalid_query: ${e.message}. Timestamps are RFC 3339 with an offset, for example 2026-09-16T12:00:00Z, and page size is 1 to 1000. An empty value is a value — clear the field rather than blanking it.`,
  },

  // The billing codes exist only where the API configures billing. Of the four, the two
  // from POST /v1/keys are the ones a user meets in the ordinary way: CreateKeyForm puts
  // them in its banner with a link to the Billing page as the action. A paid key's
  // rotation is refused the same way, and the rotate dialog says so before the click
  // when it knows the standing; this is its line when it did not. The encrypt, wrap and
  // rewrap 402s reach the dashboard only as audit rows (AUDIT_FIX_LINE below).
  billing_required: {
    status: 402, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Billing needed',
    copy: (e) =>
      e.ctx.op === 'rotateKey'
        ? `billing_required: "${e.ctx.keyName ?? 'the key'}" is a paid key, and rypt rotates a paid key only while this tenant’s billing is in good standing. It is not, so rypt did not rotate it: rewrap is refused too, so nothing could be moved off a version the rotation retires before it is destroyed, and the new version could add to what the key costs. Set billing up, or pay its open invoice, on the Billing page, then rotate again.`
        : onVersion(e) === 'retire'
          ? `billing_required: "${e.ctx.keyName ?? 'the key'}" is a paid key, and rypt retires a version of a paid key only while this tenant’s billing is in good standing, because rewrap, the only way to move data off the version before it is destroyed, is refused until then. It is not, so rypt did not retire version ${e.ctx.version ?? ''}. Set billing up, or pay its open invoice, on the Billing page, then retire it again.`
          : `billing_required: "${e.ctx.keyName ?? 'the key'}" would be a ${e.ctx.tier ?? 'paid'} key, and a paid key needs this tenant’s billing in good standing. It is not, so rypt did not create it. Set billing up, or pay its open invoice, on the Billing page, then create the key again.`,
  },

  // Only the five crypto routes can produce these, and the dashboard cannot call them.
  // If one arrives, something is wrong here, not with the request.
  invalid_base64: {
    status: 400, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: () => 'invalid_base64: only the crypto routes decode base64, and the dashboard cannot call them.',
  },
  invalid_dek_length: {
    status: 400, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: () => 'invalid_dek_length: only wrap checks a data key’s length, and the dashboard cannot call it.',
  },
  invalid_ciphertext: {
    status: 400, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'unreachable',
    title: 'Unexpected response',
    copy: () => 'invalid_ciphertext: only decrypt, unwrap and rewrap read ciphertext, and the dashboard cannot call them.',
  },

  key_exists: {
    status: 409, reauth: false, retry: 'never', surface: 'field', field: 'name', reach: 'expected',
    title: 'That name is taken',
    copy: (e) =>
      `key_exists: this tenant already has a key named "${e.ctx.keyName ?? ''}", and a deleted key keeps its name until it is destroyed. Pick another name, or use Delete now on that key’s page, which frees the name at once.`,
  },
  free_key_exists: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The free key is taken',
    copy: (e) =>
      `free_key_exists: this tenant already has a free key, so rypt did not create "${e.ctx.keyName ?? 'the key'}". A deleted free key keeps its place until it is destroyed. Create this one on a paid tier, which needs billing, or use Delete now on each of this tenant’s free keys, which frees the place at once.`,
  },
  // Containers and firewalls. A name taken lands on the name; the three refusals to delete
  // say what to remove first.
  container_exists: {
    status: 409, reauth: false, retry: 'never', surface: 'field', field: 'name', reach: 'expected',
    title: 'That name is taken',
    copy: (e) => `container_exists: this tenant already has a container named "${e.ctx.keyName ?? ''}". Pick another name.`,
  },
  firewall_exists: {
    status: 409, reauth: false, retry: 'never', surface: 'field', field: 'name', reach: 'expected',
    title: 'That name is taken',
    copy: (e) => `firewall_exists: this container already has a firewall named "${e.ctx.keyName ?? ''}". Pick another name.`,
  },
  container_not_empty: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The container is not empty',
    copy: (e) =>
      `container_not_empty: "${e.ctx.keyName ?? 'the container'}" was not deleted: ${e.message}. Use Delete now on its encryption keys, revoke its API tokens and delete its firewalls, then delete it.`,
  },
  container_is_default: {
    status: 409, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'The default container stays',
    copy: () =>
      'container_is_default: the default container cannot be deleted, and the dashboard does not offer to. Rename it instead.',
  },
  firewall_in_use: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The firewall is in use',
    copy: (e) =>
      `firewall_in_use: "${e.ctx.keyName ?? 'the firewall'}" was not deleted: ${e.message}. Set each of those API tokens to No firewall with Restrict on the API tokens page, then delete it.`,
  },
  // Deletion is reversible for seven days, and these three are its races: a rotate on a
  // key someone has since deleted, a restore of one that is no longer pending, and a
  // restore of one whose seven days ran out while the page was open. Each mutation reads
  // the key again, so the page catches up with what the copy says.
  key_pending_deletion: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The key is deleted',
    copy: (e) =>
      `key_pending_deletion: "${e.ctx.keyName ?? 'this key'}" is deleted, and refuses every operation until it is restored. Restore it on its page before the date on its page, then try again.`,
  },
  // A rotation or a retirement by hand that would take a key past the ten versions it may
  // have retiring at once. The dialogs say so before the click; this is the race, a change
  // through the API since the page read the key, and the mutation reads the key again. The
  // API's message says which case it is and what to do: wait for the next destruction,
  // whose date only it gives, or keep more versions, after a big drop in keep_at_most.
  too_many_retiring_versions: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Too many versions retiring',
    copy: (e) =>
      onVersion(e) === 'retire'
        ? `too_many_retiring_versions: rypt did not retire ${versionOf(e)}, because the key is already retiring ten versions, the most a key may have at once: ${e.message}. Meanwhile, rewrap what is still under the retiring versions.`
        : `too_many_retiring_versions: rypt did not rotate ${keyNamed(e)}, because the rotation would leave it with more versions retiring than the ten a key may have at once: ${e.message}.`,
  },
  // Neither is offered by the dashboard: it draws no Retire beside the primary, and no
  // Restore or keep-at-most setting on a free key. Each arriving means the page and the
  // key disagree, and the mutation reads the key again.
  primary_version: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'client-bug',
    title: 'That is the primary version',
    copy: (e) =>
      `primary_version: rypt did not retire ${versionOf(e)}, because it is the key’s primary version, which new encryption uses. Rotate the key first; the version is then an old one, and can be retired.`,
  },
  free_key_one_version: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'client-bug',
    title: 'A free key keeps one version',
    copy: (e) =>
      onVersion(e) === 'restore'
        ? `free_key_one_version: rypt did not restore ${versionOf(e)}, because ${keyNamed(e, 'the key')} is a free key, which keeps exactly one version, so a version it retires cannot be restored. A paid key keeps as many versions as you choose.`
        : `free_key_one_version: ${keyNamed(e)} is a free key, which keeps exactly one version, so it has no setting for how many versions to keep. A paid key keeps as many versions as you choose.`,
  },
  // A restore of a version that is no longer retiring: restored already, in another tab or
  // by an API token, since the page read the key.
  not_retiring: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The version is not retiring',
    copy: (e) =>
      `not_retiring: ${versionOf(e)} is not retiring, so there is nothing to restore. It has been restored already. The page is reading the key again.`,
  },
  not_pending_deletion: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'The key is not deleted',
    copy: (e) =>
      `not_pending_deletion: "${e.ctx.keyName ?? 'this key'}" is not pending deletion, so there is nothing to restore. It has been restored already. The page is reading it again.`,
  },
  // The API refuses a restore from destroy_after on, by its own clock, even while the key
  // is still listed: its destruction may have begun. The browser's clock can be behind,
  // so the banner hides Restore on this answer as well as on the time.
  // The same for a retiring version, from its own destroy_after on.
  restore_window_closed: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Too late to restore',
    copy: (e) =>
      onVersion(e) === 'restore'
        ? `restore_window_closed: ${versionOf(e)} is past its seven days and is being destroyed, so it can no longer be restored, and anything still encrypted under it will not decrypt once it is gone. Nothing in the dashboard can bring it back.`
        : `restore_window_closed: "${e.ctx.keyName ?? 'this key'}" is past its seven days and is being destroyed, so it can no longer be restored. Nothing in the dashboard can bring it back.`,
  },
  // Both of these mean the standing on the Billing page was stale when the button was
  // pressed; the page refetches it, and the copy says what the fresh standing will show.
  // billing_active covers unpaid as well as good standing: the subscription is still open,
  // and a second one beside it would bill every key twice.
  billing_active: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Billing is already set up',
    copy: () =>
      'billing_active: this tenant already has an open subscription — active, past_due or unpaid — so there is nothing to check out. Change the card, pay an open invoice, see invoices or cancel through Manage billing.',
  },
  billing_not_set_up: {
    status: 409, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Billing is not set up',
    copy: () =>
      'billing_not_set_up: this tenant has no billing to manage yet — Stripe has no customer for it. Set it up first with Set up billing.',
  },
  payload_too_large: {
    status: 413, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'Body too large',
    copy: () => 'payload_too_large: the dashboard only ever sends a name and a tier, so this is a dashboard fault.',
  },
  unsupported_media_type: {
    status: 415, reauth: false, retry: 'never', surface: 'bug-banner', reach: 'client-bug',
    title: 'Wrong content type',
    copy: () => 'unsupported_media_type: rypt wants content-type: application/json and the dashboard did not send it.',
  },

  // Two very different 429s. ops_cap_reached is a fact about the month and retrying is
  // pointless; rate_limited is transient. Neither carries Retry-After. A free key at its
  // cap refuses encrypt, wrap, rewrap and rotate only: decrypt and unwrap go on.
  ops_cap_reached: {
    status: 429, reauth: false, retry: 'never', surface: 'banner', reach: 'expected',
    title: 'Monthly cap reached',
    copy: (e) =>
      e.ctx.op === 'rotateKey'
        ? `ops_cap_reached: "${e.ctx.keyName ?? 'this key'}" is a free key that has used all ${num.format(e.cap ?? 0)} operations for this month, so rypt did not rotate it: rewrap is refused at the cap too, so nothing could be moved off the version a rotation retires before it is destroyed. Decrypt and unwrap keep working. Rotate it once the counter resets ${resetsPhrase(e)}.`
        : onVersion(e) === 'retire'
          ? `ops_cap_reached: "${e.ctx.keyName ?? 'this key'}" is a free key that has used all ${num.format(e.cap ?? 0)} operations for this month, so rypt did not retire version ${e.ctx.version ?? ''}: rewrap is refused at the cap too, so nothing could be moved off it before it is destroyed. Retire it once the counter resets ${resetsPhrase(e)}.`
          : `ops_cap_reached: "${e.ctx.keyName ?? 'this key'}" has used all ${num.format(e.cap ?? 0)} operations for this month, so encrypt, wrap and rewrap on it are refused until the counter resets ${resetsPhrase(e)}; decrypt and unwrap keep working. A paid key — wrapped, software or hsm — runs past its included operations and records overage.`,
  },
  rate_limited: {
    status: 429, reauth: false, retry: 'blind-backoff', surface: 'toast', reach: 'expected',
    title: 'Too many requests',
    copy: () =>
      'rate_limited: over 100 requests a second for this account. rypt sends no Retry-After, so the dashboard is backing off on its own.',
    finalCopy: 'rate_limited: rypt is still shedding load. Retry, or come back in a minute.',
  },

  internal_error: {
    status: 500, reauth: false, retry: 'once', surface: 'toast', reach: 'expected',
    title: 'rypt failed',
    copy: (e) =>
      `internal_error: ${e.ctx.op} failed inside rypt. Whether it took effect is not knowable from here — reload before trying again.`,
  },
  unavailable: {
    status: 503, reauth: false, retry: 'blind-backoff', surface: 'toast', reach: 'expected',
    title: 'rypt is shedding load',
    copy: () =>
      'unavailable: rypt cannot reach its identity provider, or an internal queue was full. Your sign-in is fine — rypt’s own check of it is not. Retrying.',
    finalCopy:
      'unavailable: rypt still cannot reach its identity provider, or a queue inside it is full. Your sign-in is fine. Retry, or come back in a few minutes.',
  },
} satisfies Record<ErrorCode, CodeBehaviour>;

/** Failures that never carry an API error code. */
export const NON_API_BEHAVIOUR = {
  transport: {
    status: 0, reauth: false, retry: 'blind-backoff', surface: 'banner', reach: 'expected',
    title: 'rypt could not be reached',
    copy: () =>
      'The request never got an answer: the network is down, or the browser refused the response. Nothing was sent twice.',
  },
  opaque_token: {
    status: 0, reauth: false, retry: 'never', surface: 'app-page', reach: 'client-bug',
    title: 'Auth0 returned an opaque token',
    copy: () =>
      'The token Auth0 issued carries no audience, so rypt cannot verify it. Signing in again would produce another one. This build is misconfigured: the SPA application must request https://api.rypt.dev.',
  },
  wrong_audience: {
    status: 0, reauth: false, retry: 'never', surface: 'app-page', reach: 'client-bug',
    title: 'The token is for another API',
    copy: () =>
      'Auth0 issued a token whose audience is not https://api.rypt.dev. Signing in again would produce another one. Check the API identifier in the Auth0 tenant.',
  },
  interaction_required: {
    status: 0, reauth: true, retry: 'never', surface: 'redirect', reach: 'expected',
    title: 'Signing in again',
    copy: () => 'The session could not be renewed silently. Signing in again.',
  },
} satisfies Record<'transport' | 'opaque_token' | 'wrong_audience' | 'interaction_required', CodeBehaviour>;

const ALL = { ...ERROR_BEHAVIOUR, ...NON_API_BEHAVIOUR } as Record<string, CodeBehaviour>;

export function behaviourFor(e: RyptError): CodeBehaviour {
  return ALL[e.code] ?? NON_API_BEHAVIOUR.transport;
}

export const messageFor = (e: RyptError): string => behaviourFor(e).copy(e);
export const titleFor = (e: RyptError): string => behaviourFor(e).title;

/**
 * The message for an error the query has stopped retrying: the code's final line where it
 * has one, which no longer says a retry is under way, and otherwise the usual message.
 */
export const finalMessageFor = (e: RyptError): string => behaviourFor(e).finalCopy ?? messageFor(e);

/* ---- audit rows ----
 *
 * `audit_events.error_code` is a plain string in the spec, not an enum, so this is a
 * partial map with a fallback rather than an exhaustive one: rypt may record a code this
 * build has never heard of, and printing it bare is better than printing nothing.
 *
 * These lines are written for someone reading their own failed operation in the log, so
 * each one is a thing to change in the terminal — not an explanation of the status code.
 * The 401 and 403 codes are deliberately absent: an operation that fails authentication
 * never reaches the audit log at all, so a fix line for one here would be unreachable
 * copy claiming to explain a row that cannot exist.
 */
export const AUDIT_FIX_LINE: Partial<Record<ErrorCode, string>> = {
  invalid_base64:
    'plaintext and ciphertext are base64. Encode the bytes before sending them, and send the encoded string, not a JSON array.',
  invalid_ciphertext:
    'The ciphertext was not produced by this key, was altered in transit, or names a version a rotation retired and rypt has since destroyed, which nothing can decrypt again. Send back exactly what encrypt returned.',
  invalid_dek_length: 'wrap takes a 16, 24 or 32-byte data key. Check the length before encoding it.',
  invalid_request: 'The request body was rejected. Check the field names against the OpenAPI document.',
  not_found: 'The key id in the request is not a live key in this tenant. Copy the Key id on an encryption key’s page.',
  key_pending_deletion:
    'The key is deleted, and refuses every operation, decrypt included, until it is restored. If something still needs it, restore it on its page before the date on its page.',
  // A refused rotate is audited, with nothing created, and so is a refused retire_version:
  // this on either row.
  too_many_retiring_versions:
    'The key would have had more than ten versions retiring, the most a key may have at once, so nothing was created or retired. Rewrap what is still under the retiring versions meanwhile, and try again once the next one is destroyed: the key’s Versions tab gives each one’s date. A rotation that would retire many versions at once, after keep_at_most was lowered a long way, needs keep_at_most raised instead.',
  primary_version:
    'The version named is the key’s primary, which new encryption uses, so it was not retired. Rotate the key first, then retire the old primary.',
  not_retiring:
    'The version named was not retiring, so there was nothing to restore. Read the key: its retiring_versions lists those that can be restored.',
  free_key_one_version:
    'A free key keeps exactly one version, so it has no retiring version to restore and no keep_at_most setting.',
  invalid_keep_at_most:
    'keep_at_most is required, and is a whole number from 1 up, or null to keep every version until it is retired by hand.',
  payload_too_large:
    'The body exceeded the limit for this tier. hsm caps plaintext plus aad at 8,192 bytes; split the input.',
  unsupported_media_type: 'Send content-type: application/json.',
  ops_cap_reached:
    'The free key reached its monthly cap, so encrypt, wrap, rewrap and rotate on it are refused until the counter resets on the 1st, 00:00 UTC; decrypt and unwrap keep working. Wait for the reset, or use a paid key — wrapped, software or hsm — which records overage instead.',
  billing_required:
    'This is a paid key and the tenant’s billing is not in good standing, so encrypt, wrap, rewrap and rotate are refused; decrypt and unwrap still work. Set billing up, or pay its open invoice, on the Billing page.',
  rate_limited: 'Over 100 requests a second for this account. Slow the caller down; rypt sends no Retry-After.',
  internal_error: 'The operation failed inside rypt. Whether it took effect is not knowable from the log alone.',
  unavailable: 'rypt was shedding load or could not reach its identity provider. Run it again.',
};

/** The fix line for an audit row's error code, or null when there is nothing useful to add. */
export const auditFixLine = (code: string | null): string | null =>
  code === null ? null : (AUDIT_FIX_LINE[code as ErrorCode] ?? null);
